import { createPortal } from "react-dom";

const PrivacyPolicy = () => {
  return createPortal(
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      backgroundColor: "#fff", overflowY: "auto",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      color: "#374151", fontSize: 14, lineHeight: 1.7,
    }}>

      {/* Header */}
      <div style={{ backgroundColor: "#075e54", padding: "20px 24px" }}>
        <h1 style={{ color: "#fff", fontSize: 22, fontWeight: 700, margin: 0 }}>Privacy Policy</h1>
        <p style={{ color: "#a7f3d0", fontSize: 13, marginTop: 4, marginBottom: 0 }}>WA Promo — by FinGlider</p>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "40px 24px 60px" }}>
        <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 24 }}>Last updated: July 2026</p>

        <p style={{ marginBottom: 32 }}>
          FinGlider ("we", "us", or "our") operates WA Promo, a WhatsApp Business marketing
          platform available at <strong>https://wapromo.finglider.com</strong>. This Privacy Policy
          explains how we collect, use, and protect information when you use our service.
        </p>

        <Section title="1. Information We Collect">
          <p>We collect the following types of information:</p>
          <ul style={{ paddingLeft: 20, marginTop: 8 }}>
            <li><strong>Account information:</strong> Name, email address, and password when you register.</li>
            <li><strong>WhatsApp Business credentials:</strong> Phone Number ID, WABA ID, and access tokens you provide to connect to the Meta WhatsApp Cloud API.</li>
            <li><strong>Contact data:</strong> Customer names, phone numbers, and locations you import for sending WhatsApp messages.</li>
            <li><strong>Message data:</strong> Templates you create, broadcasts you send, and delivery/read status updates from the Meta API.</li>
            <li><strong>Usage data:</strong> Log data, feature usage, and technical information about your interactions with the platform.</li>
          </ul>
        </Section>

        <Section title="2. How We Use Your Information">
          <ul style={{ paddingLeft: 20 }}>
            <li>To provide and operate the WA Promo service.</li>
            <li>To send WhatsApp template messages to your customers via the Meta WhatsApp Cloud API.</li>
            <li>To display message delivery, read, and reply statistics on your dashboard.</li>
            <li>To manage your subscription and account settings.</li>
            <li>To respond to support requests and improve the platform.</li>
          </ul>
        </Section>

        <Section title="3. WhatsApp and Meta Platform">
          <p>
            WA Promo integrates with the Meta WhatsApp Business Cloud API. By using our platform,
            you agree to comply with WhatsApp's Business Policy and Meta's Platform Terms.
          </p>
          <p style={{ marginTop: 8 }}>
            We access the WhatsApp API only to perform actions you initiate — sending templates,
            receiving delivery callbacks, and managing opt-in/opt-out requests. We do not use
            your credentials for any other purpose.
          </p>
        </Section>

        <Section title="4. Opt-Out Handling">
          <p>
            When a customer replies with "STOP" or taps the opt-out button on a template, WA Promo
            marks that customer as inactive and sends an unsubscribe confirmation. They won't
            receive further messages until they reply "START".
          </p>
        </Section>

        <Section title="5. Data Storage and Security">
          <p>
            All data is stored on secure servers using HTTPS/TLS, access controls, and secure
            credential storage. WhatsApp access tokens are stored in encrypted form and used solely
            to communicate with the Meta API.
          </p>
          <p style={{ marginTop: 8 }}>
            We retain your data for as long as your account is active. Upon account deletion,
            your data is removed within 30 days.
          </p>
        </Section>

        <Section title="6. Data Sharing">
          <p>We do not sell, trade, or rent your personal information. We may share data with:</p>
          <ul style={{ paddingLeft: 20, marginTop: 8 }}>
            <li><strong>Meta Platforms:</strong> To deliver WhatsApp messages via the Cloud API.</li>
            <li><strong>Infrastructure providers:</strong> Cloud hosting and database services.</li>
            <li><strong>Legal requirements:</strong> When required by law or to protect our rights.</li>
          </ul>
        </Section>

        <Section title="7. Your Rights">
          <ul style={{ paddingLeft: 20 }}>
            <li>Access, correct, or delete your personal data.</li>
            <li>Export your customer contact data.</li>
            <li>Disconnect your WhatsApp Business account at any time.</li>
            <li>Close your account and request deletion of all associated data.</li>
          </ul>
          <p style={{ marginTop: 8 }}>To exercise these rights, contact us at the email below.</p>
        </Section>

        <Section title="8. Cookies">
          <p>WA Promo uses session cookies only for authentication. We do not use tracking or advertising cookies.</p>
        </Section>

        <Section title="9. Children's Privacy">
          <p>WA Promo is a business tool for adults. We do not knowingly collect information from anyone under 18.</p>
        </Section>

        <Section title="10. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. Changes will be posted here with
            an updated date. Continued use of the platform constitutes acceptance of the updated policy.
          </p>
        </Section>

        <Section title="11. Contact Us">
          <p>If you have questions about this Privacy Policy, please contact us:</p>
          <div style={{
            background: "#f9fafb", border: "1px solid #e5e7eb",
            borderRadius: 8, padding: 16, marginTop: 10, lineHeight: 1.8,
          }}>
            <strong>FinGlider</strong><br />
            Email: <a href="mailto:info@finglider.com" style={{ color: "#065f46" }}>info@finglider.com</a><br />
            Platform: <a href="https://wapromo.finglider.com" style={{ color: "#065f46" }}>https://wapromo.finglider.com</a>
          </div>
        </Section>
      </div>

      {/* Footer */}
      <div style={{ borderTop: "1px solid #f3f4f6", padding: 20, textAlign: "center", fontSize: 12, color: "#9ca3af" }}>
        © {new Date().getFullYear()} FinGlider. All rights reserved.
      </div>
    </div>,
    document.body
  );
};

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 28 }}>
    <h2 style={{ fontSize: 15, fontWeight: 600, color: "#111827", marginBottom: 8 }}>{title}</h2>
    {children}
  </div>
);

export default PrivacyPolicy;
